import Link from 'next/link';
import { useRouter } from 'next/router';
import { motion } from 'framer-motion';
import { useI18nSafe } from '../context/I18nContext';
import { canonicalizePath, localizePath } from '../lib/localizedRoutes';

const ROUTE_TITLE_KEYS = {
  '/about': 'titlebar.routes.about',
  '/services': 'titlebar.routes.services',
  '/promotions': 'titlebar.routes.promotions',
  '/contact': 'titlebar.routes.contact',
  '/privacy': 'titlebar.routes.privacy',
  '/terms': 'titlebar.routes.terms',
  '/legal': 'titlebar.routes.legal',
  '/machines/categories': 'titlebar.routes.productCategories',
  '/machines': 'titlebar.routes.products',
  '/machines/[id]': 'titlebar.routes.productDetails',
  '/spare-parts': 'titlebar.routes.spareParts',
  '/spare-parts/[id]': 'titlebar.routes.sparePartDetails',
  '/account': 'titlebar.routes.account',
  '/cart': 'titlebar.routes.cart',
  '/auth/login': 'titlebar.routes.login',
  '/auth/signup': 'titlebar.routes.signup',
  '/auth/forgot-password': 'titlebar.routes.forgotPassword',
  '/auth/reset-password': 'titlebar.routes.resetPassword',
};

function toTitleCase(segment) {
  return segment
    .replace(/[\[\]]/g, '')
    .replace(/-/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function resolveSegmentLabel(pathnameSlice, t) {
  if (ROUTE_TITLE_KEYS[pathnameSlice]) return t(ROUTE_TITLE_KEYS[pathnameSlice]);
  const parts = pathnameSlice.split('/').filter(Boolean);
  const last = parts[parts.length - 1] || '';
  if (last.startsWith('[') && last.endsWith(']')) return 'Details';
  return toTitleCase(last);
}

function toReadableTypeLabel(raw) {
  return String(raw || '')
    .trim()
    .replace(/_/g, ' ')
    .replace(/-/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

const containerVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      staggerChildren: 0.05,
      delayChildren: 0.05,
      ease: [0.25, 0.1, 0.25, 1],
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 10 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: [0.25, 0.1, 0.25, 1] },
  },
};

export default function TitleBar() {
  const router = useRouter();
  const { t, language } = useI18nSafe();
  const { query } = router;
  const pathname = canonicalizePath(router.pathname || router.asPath || '/').split('?')[0] || '/';

  if (pathname === '/') return null;

  const segments = pathname.split('/').filter(Boolean);
  const isCategoriesPage = pathname === '/machines/categories';
  const isProductsPage = pathname === '/machines';
  const isFilteredProducts = pathname === '/machines' && typeof query.productType === 'string' && query.productType.trim();
  const isProductDetails = pathname === '/machines/[id]';
  const rawTypeKey = typeof query.productType === 'string' ? query.productType.trim() : '';
  const rawTypeLabelParam = typeof query.productTypeLabel === 'string' ? query.productTypeLabel.trim() : '';
  const selectedTypeLabelRaw =
    language === 'en' ? rawTypeKey || rawTypeLabelParam : rawTypeLabelParam || rawTypeKey;
  const selectedTypeLabel = toReadableTypeLabel(selectedTypeLabelRaw);
  const productName = typeof query.productName === 'string' ? query.productName.trim() : '';
  const title = isCategoriesPage
    ? t('productsPage.hero.categories')
    : isProductDetails && productName
    ? productName
    : isFilteredProducts
    ? selectedTypeLabel
    : ROUTE_TITLE_KEYS[pathname]
      ? t(ROUTE_TITLE_KEYS[pathname])
      : resolveSegmentLabel(pathname, t);
  const titleWords = String(title || '').trim().split(/\s+/).filter(Boolean);

  const defaultCrumbs = segments.map((segment, index) => {
    const patternParts = segments.slice(0, index + 1);
    const patternPath = `/${patternParts.join('/')}`;
    const href = `/${patternParts
      .map((part) => {
        if (part.startsWith('[') && part.endsWith(']')) {
          const key = part.slice(1, -1);
          return query[key] || '';
        }
        return part;
      })
      .filter(Boolean)
      .join('/')}`;

    return {
      key: patternPath,
      href,
      label: resolveSegmentLabel(patternPath, t),
    };
  });
  const crumbs = isCategoriesPage
    ? [
        {
          key: '/machines/categories',
          href: localizePath('/machines/categories', language),
          label: t('productsPage.hero.categories'),
        },
      ]
    : isFilteredProducts
    ? [
        {
          key: '/machines/categories',
          href: localizePath('/machines/categories', language),
          label: t('productsPage.hero.categories'),
        },
        {
          key: '/machines/type',
          href: localizePath(router.asPath || '/machines', language),
          label: selectedTypeLabel || t('common.general'),
        },
      ]
    : isProductsPage
    ? [
        {
          key: '/machines/categories',
          href: localizePath('/machines/categories', language),
          label: t('productsPage.hero.categories'),
        },
      ]
    : isProductDetails
    ? [
        {
          key: '/machines/categories',
          href: localizePath('/machines/categories', language),
          label: t('productsPage.hero.categories'),
        },
        {
          key: '/machines/type',
          href: localizePath(
            selectedTypeLabelRaw
              ? `/machines?productType=${encodeURIComponent(String(query.productType || ''))}&productTypeLabel=${encodeURIComponent(String(selectedTypeLabel || ''))}`
              : '/machines',
            language,
          ),
          label: selectedTypeLabel || t('common.general'),
        },
        {
          key: '/machines/details',
          href: localizePath(router.asPath || '/machines', language),
          label: productName || t('titlebar.routes.productDetails'),
        },
      ]
    : defaultCrumbs.map((crumb) => ({ ...crumb, href: localizePath(crumb.href, language) }));

  return (
    <section
      key={`${pathname}:${router.asPath || ''}`}
      className="relative h-[300px] overflow-hidden bg-zinc-900 md:h-[350px]"
    >
      <div className="absolute inset-0 z-0">
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src="/title/1.webp"
          alt="Construction site"
          className="h-full w-full object-cover grayscale"
        />
        <div className="absolute inset-0 bg-black/40" />
        <div className="absolute inset-0 bg-gradient-to-r from-orange-700/70 via-orange-600/35 to-black/15" />
        <motion.div
          aria-hidden="true"
          className="absolute inset-0 bg-[radial-gradient(circle_at_15%_20%,rgba(255,255,255,0.18)_0%,transparent_28%),radial-gradient(circle_at_78%_30%,rgba(228,188,155,0.18)_0%,transparent_22%)]"
          initial={{ opacity: 0, scale: 1.05 }}
          animate={{ opacity: [0, 0.9, 0.45], scale: [1.06, 1, 1.02] }}
          transition={{ duration: 1.2, ease: 'easeOut' }}
        />
        <div className="pointer-events-none absolute inset-0 z-[1] overflow-hidden">
          <motion.div
            aria-hidden="true"
            initial={{ x: -280, opacity: 0 }}
            animate={{ x: 0, opacity: 0.45 }}
            transition={{ duration: 0.9, ease: 'easeOut', delay: 0.08 }}
            className="absolute left-[2%] top-0 h-full w-[30%]"
          >
            <motion.div
              animate={{ x: [0, 18, 0] }}
              transition={{ duration: 6, ease: 'easeInOut', repeat: Infinity, delay: 0.98 }}
              className="h-full w-full"
            >
              <div className="h-full w-full origin-left skew-x-[-10deg]" style={{ backgroundColor: 'rgba(var(--dash-secondary-rgb), 0.75)' }} />
            </motion.div>
          </motion.div>
          <motion.div
            aria-hidden="true"
            initial={{ x: -220, opacity: 0 }}
            animate={{ x: 0, opacity: 0.52 }}
            transition={{ duration: 0.9, ease: 'easeOut', delay: 0.18 }}
            className="absolute -left-[30%] top-0 h-full w-[40%]"
          >
            <motion.div
              animate={{ x: [0, 22, 0] }}
              transition={{ duration: 6, ease: 'easeInOut', repeat: Infinity, delay: 1.08 }}
              className="h-full w-full"
            >
              <div className="h-full w-full origin-left skew-x-[-10deg]" style={{ backgroundColor: 'rgba(var(--dash-main-rgb), 1)' }} />
            </motion.div>
          </motion.div>
          <motion.div
            aria-hidden="true"
            initial={{ x: 220, opacity: 0 }}
            animate={{ x: 0, opacity: 0.52 }}
            transition={{ duration: 0.9, ease: 'easeOut', delay: 0.28 }}
            className="absolute -right-[40%] top-0 h-full w-[40%]"
          >
            <motion.div
              animate={{ x: [0, -22, 0] }}
              transition={{ duration: 6, ease: 'easeInOut', repeat: Infinity, delay: 1.18 }}
              className="h-full w-full"
            >
              <div className="h-full w-full origin-left skew-x-[-10deg]" style={{ backgroundColor: 'rgba(var(--dash-other-rgb), .8)' }} />
            </motion.div>
          </motion.div>
        </div>
      </div>

      <div className="container relative z-10 mx-auto flex h-full items-center px-4 lg:px-8">
        <motion.div className="max-w-3xl" variants={containerVariants} initial="hidden" animate="visible">
          <motion.div className="mb-6 inline-flex max-w-full items-center rounded-2xl" variants={itemVariants}>
            <div className="flex flex-wrap items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-white">
              <motion.div variants={itemVariants}>
                <Link href="/" className="rounded-lg px-2 py-1 text-white/88 transition-colors duration-200 hover:text-white">
                  {t('common.home')}
                </Link>
              </motion.div>
              {crumbs.map((crumb, index) => {
                const isLast = index === crumbs.length - 1;
                return (
                  <motion.span key={crumb.key} className="inline-flex items-center gap-2" variants={itemVariants}>
                    <span className="text-white/45">/</span>
                    {isLast ? (
                      <motion.span
                        className="rounded-lg border border-white/25 bg-white/12 px-2 py-1 text-white/96 shadow-sm"
                        whileHover={{ y: -1 }}
                        transition={{ duration: 0.2 }}
                      >
                        {crumb.label}
                      </motion.span>
                    ) : (
                      <Link href={crumb.href} className="rounded-lg px-2 py-1 text-white/88 transition-colors duration-200 hover:text-white">
                        {crumb.label}
                      </Link>
                    )}
                  </motion.span>
                );
              })}
            </div>
          </motion.div>

          <motion.h1
            className="mb-4 text-3xl font-bold uppercase leading-none tracking-tight text-white drop-shadow-xl md:text-6xl"
            variants={containerVariants}
          >
            {titleWords.length > 0
              ? titleWords.map((word, index) => (
                  <motion.span
                    key={`${word}-${index}`}
                    className="mr-2 inline-block"
                    variants={itemVariants}
                  >
                    {word}
                  </motion.span>
                ))
              : title}
          </motion.h1>
        </motion.div>
      </div>

      <motion.div
        className="absolute bottom-6 right-10 z-20 hidden items-center gap-3 font-mono text-[9px] text-white opacity-30 md:flex"
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 0.3, y: 0 }}
        transition={{ duration: 0.65, delay: 0.25 }}
      >
        <motion.div
          className="h-2 w-2 rounded-full bg-orange-500"
          animate={{ scale: [1, 1.12, 1], opacity: [0.45, 0.8, 0.45] }}
          transition={{ duration: 2.6, repeat: Infinity, ease: 'easeInOut' }}
        />
      </motion.div>
    </section>
  );
}
